import React, { useState, useEffect, useRef } from "react";
import { auth, db, storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc, setDoc, getDoc, deleteDoc, addDoc, updateDoc,
  collection, onSnapshot, query, where
} from "firebase/firestore";

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════

const INTERESTS = [
  "Jazz Guitar", "Hip-Hop", "Basketball", "Rock Climbing", "Film",
  "Cooking", "Philosophy", "Startups", "Photography", "Skateboarding",
  "Anime", "Hiking", "Poetry", "Gaming", "Travel", "Vinyl Records",
  "Soccer", "Coding", "Fashion", "Surfing", "Yoga", "Chess",
  "Sneakers", "Thrifting", "Astronomy", "MMA", "DJing", "Crypto"
];

const SHIRT_COLORS = [
  { name: "Black", hex: "#1A1A1A", border: "#444" },
  { name: "White", hex: "#F5F5F5", border: "#ccc" },
  { name: "Red", hex: "#E94560", border: "#E94560" },
  { name: "Blue", hex: "#3B82F6", border: "#3B82F6" },
  { name: "Green", hex: "#22C55E", border: "#22C55E" },
  { name: "Grey", hex: "#6B7280", border: "#6B7280" },
  { name: "Navy", hex: "#1E3A5F", border: "#2D5A8E" },
  { name: "Pink", hex: "#EC4899", border: "#EC4899" },
  { name: "Orange", hex: "#F97316", border: "#F97316" },
  { name: "Purple", hex: "#8B5CF6", border: "#8B5CF6" },
  { name: "Brown", hex: "#92400E", border: "#B45309" },
  { name: "Yellow", hex: "#EAB308", border: "#EAB308" },
];

const CAMPUS_SPOTS = [
  {name:"Curry Student Center — Main Lounge",lat:42.339055109972605,lng:-71.08752848144701},
  {name:"Snell Library — 2nd Floor Atrium",lat:42.33857940219052,lng:-71.08802987244069},
  {name:"International Village — Lobby",lat:42.33544655276953,lng:-71.0890002785609},
  {name:"Tatte Bakery & Cafe",lat:42.34006360548192,lng:-71.09046625038545},
  {name:"Curry Student Center — 2nd Floor",lat:42.339055109972605,lng:-71.08752848144701},
  {name:"Starbucks — Huntington Ave",lat:42.339007124372344,lng: -71.08728022199837},
  {name:"Krentzman Quad — Benches",lat:42.34009978670498,lng:-71.08826383500922},
  {name:"Centenial Common",lat:42.33708922216236,lng:-71.09051571929302},
  {name:"El Jefes",lat:42.34210047131995,lng:-71.08608426862932},
];


const ICE_BREAKERS = {
  "Jazz Guitar": "You both love jazz guitar — ask them who their favorite player is right now.",
  "Hip-Hop": "You both love hip-hop — ask who dropped the best album this year.",
  "Basketball": "You both follow basketball — ask them for their hottest take this season.",
  "Rock Climbing": "You're both climbers — ask them about their latest project on the wall.",
  "Film": "You're both into film — ask what the last movie was that genuinely surprised them.",
  "Cooking": "You both cook — ask them what dish they've been perfecting lately.",
  "Philosophy": "You both dig philosophy — ask them a question they can't stop thinking about.",
  "Startups": "You're both into startups — ask what problem they'd solve with unlimited funding.",
  "Photography": "You both shoot photos — ask about the best photo they've taken this month.",
  "Skateboarding": "You both skate — ask them about their favorite spot on campus.",
  "Anime": "You both watch anime — ask what they're watching right now.",
  "Hiking": "You both hike — ask them about the best trail they've done recently.",
  "Poetry": "You both appreciate poetry — ask who they've been reading lately.",
  "Gaming": "You're both gamers — ask what they've been playing and if it's any good.",
  "Travel": "You both love travel — ask about the best place they've ever gotten lost.",
  "Vinyl Records": "You both collect vinyl — ask what their latest find was.",
  "Soccer": "You both follow soccer — ask them who their team is and prepare for a debate.",
  "Coding": "You both code — ask what they're building right now.",
  "Fashion": "You're both into fashion — ask what piece they're most proud of owning.",
  "Surfing": "You both surf — ask them about the best wave they've ever caught.",
  "Yoga": "You both practice yoga — ask them where their favorite spot to practice is.",
  "Chess": "You both play chess — ask them their opening of choice.",
  "Sneakers": "You're both into sneakers — ask what their grail pair is.",
  "Thrifting": "You both thrift — ask about their best find ever.",
  "Astronomy": "You both love astronomy — ask them about the coolest thing they've seen in the sky.",
  "MMA": "You both follow MMA — ask them who wins the next big fight.",
  "DJing": "You both DJ — ask what genre they'd play to close out a set.",
  "Crypto": "You're both into crypto — ask what project they're most excited about.",
};

// ══════════════════════════════════════
//  STYLES
// ══════════════════════════════════════

const S = {
  screen: {
    minHeight: "100vh",
    width: "100%",
    maxWidth: 480,
    margin: "0 auto",
    background: "linear-gradient(180deg, #0A0A1A 0%, #12122A 100%)",
    padding: "28px",
    position: "relative",
  },
  btn: {
    width: "100%",
    padding: "14px",
    borderRadius: 14,
    border: "none",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    letterSpacing: 0.3,
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #E94560 0%, #C73659 100%)",
    boxShadow: "0 4px 25px rgba(233, 69, 96, 0.3)",
  },
  btnSecondary: {
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 12,
    border: "1.5px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontSize: 15,
    transition: "border-color 0.2s",
  },
  label: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    display: "block",
    marginBottom: 8,
  },
  title: {
    color: "#fff",
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: -0.5,
    marginBottom: 8,
    fontFamily: "'Playfair Display', Georgia, serif",
  },
  subtitle: {
    color: "rgba(255,255,255,0.45)",
    fontSize: 14,
    lineHeight: 1.6,
    marginBottom: 32,
  },
  stepLabel: {
    color: "#E94560",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 2,
    marginBottom: 8,
  },
  error: {
    color: "#E94560",
    fontSize: 13,
    marginTop: 8,
    padding: "8px 12px",
    background: "rgba(233,69,96,0.1)",
    borderRadius: 8,
  },
  success: {
    color: "#2ED589",
    fontSize: 13,
    padding: "12px 16px",
    background: "rgba(46,213,137,0.1)",
    border: "1px solid rgba(46,213,137,0.2)",
    borderRadius: 12,
    marginBottom: 20,
  },
  link: {
    color: "#E94560",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
  },
};

// ══════════════════════════════════════
//  HELPER FUNCTIONS
// ══════════════════════════════════════
function Avatar({ name, photoURL, size = 48 }) {
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={name}
        style={{
          width: size, height: size, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${avatarColor(name)}, ${avatarColor(name)}cc)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.42, fontWeight: 700, color: "#fff", flexShrink: 0,
    }}>
      {name[0]}
    </div>
  );
}

function VerifiedBadge() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 3,
      background: "rgba(46,213,137,0.15)", color: "#2ED589",
      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
    }}>
      <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="8" fill="#2ED589" />
        <path d="M5 8.5L7 10.5L11 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Verified
    </span>
  );
}

function getDistanceMiles(lat1, lon1, lat2, lon2) {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getWalkTime(miles) {
  const mins = Math.round(miles / 0.05);
  return mins <= 1 ? "1 min walk" : `${mins} min walk`;
}

function avatarColor(name) {
  const colors = ["#E94560", "#0F3460", "#533483", "#16213E", "#1A5276", "#7D3C98", "#2E86C1", "#D35400"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getIceBreaker(interest) {
  return ICE_BREAKERS[interest] || "You've got something in common — go find out what else you share.";
}

function assignMeetupSpot(lat1, lng1, lat2, lng2) {
  const midLat = (lat1 + lat2) / 2;
  const midLng = (lng1 + lng2) / 2;
  const sorted = [...CAMPUS_SPOTS].sort((a, b) =>
    getDistanceMiles(midLat, midLng, a.lat, a.lng) - getDistanceMiles(midLat, midLng, b.lat, b.lng)
  );
  return sorted[Math.floor(Math.random() * Math.min(3, sorted.length))];
}

function getSharedInterests(a, b) {
  return (a || []).filter(i => (b || []).includes(i));
}

function GlobalStyles() {
  return (
    <style>{`
      @keyframes pulse {
        0% { opacity: 0.6; transform: translate(-50%, -50%) scale(0.8); }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(1.4); }
      }
      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  );
}

// ══════════════════════════════════════
//  SCREEN: Splash
// ══════════════════════════════════════

function SplashScreen({ onSignUp, onLogIn }) {
  return (
    <div style={{
      ...S.screen,
      display: "flex", flexDirection: "column",
      justifyContent: "center", alignItems: "center", textAlign: "center",
    }}>
      <div style={{
        fontSize: 56, fontWeight: 800, letterSpacing: -2,
        background: "linear-gradient(135deg, #FFFFFF 0%, #E94560 100%)",
        WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif",
      }}>
        sidequest
      </div>
      <p style={{
        color: "#E94560", fontSize: 15, fontStyle: "italic",
        letterSpacing: 1.5, marginBottom: 60, fontWeight: 300,
      }}>
        friend time, not phone time.
      </p>
      <button onClick={onSignUp} style={{ ...S.btn, ...S.btnPrimary, width: 260, marginBottom: 12 }}>
        Create Account
      </button>
      <button onClick={onLogIn} style={{ ...S.btn, ...S.btnSecondary, width: 260 }}>
        I Already Have One
      </button>
      <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, marginTop: 40 }}>
        Your city is full of interesting people.
      </p>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Sign Up
// ══════════════════════════════════════

function SignUpScreen({ onSuccess, onSwitchToLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isEdu = email.toLowerCase().endsWith(".edu");
  const passwordsMatch = password === confirm && password.length >= 6;

  const handleSignUp = async () => {
    setError("");
    if (!isEdu) { setError("Must be a .edu email"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (!passwordsMatch) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
     const cred = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(cred.user, {
        url: "https://sidequestmeet.com",
        handleCodeInApp: false,
      });
      onSuccess(cred.user);
    } catch (err) {
      setError(err.code === "auth/email-already-in-use" ? "Account already exists" : err.message);
    }
    setLoading(false);
  };

  return (
    <div style={S.screen}>
      <p style={S.stepLabel}>SIGN UP</p>
      <h2 style={S.title}>Join the Quest</h2>
      <p style={S.subtitle}>You need a .edu email to verify you're a real college student.</p>

      <label style={S.label}>University Email</label>
      <input
        type="email"
        placeholder="you@university.edu"
        value={email}
        onChange={e => setEmail(e.target.value)}
        style={{ ...S.input, marginBottom: 16, borderColor: email && !isEdu ? "#E94560" : undefined }}
      />

      <label style={S.label}>Password</label>
      <input
        type="password"
        placeholder="At least 6 characters"
        value={password}
        onChange={e => setPassword(e.target.value)}
        style={{ ...S.input, marginBottom: 16 }}
      />

      <label style={S.label}>Confirm Password</label>
      <input
        type="password"
        placeholder="Confirm your password"
        value={confirm}
        onChange={e => setConfirm(e.target.value)}
        style={{ ...S.input, marginBottom: 8 }}
      />

      {error && <p style={S.error}>{error}</p>}

      <button
        onClick={handleSignUp}
        disabled={loading || !isEdu || !passwordsMatch}
        style={{
          ...S.btn, ...S.btnPrimary, marginTop: 24,
          opacity: (isEdu && passwordsMatch && !loading) ? 1 : 0.4,
        }}
      >
        {loading ? "Creating Account..." : "Create Account"}
      </button>

      <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, textAlign: "center", marginTop: 24 }}>
        Already have an account?{" "}
        <button onClick={onSwitchToLogin} style={S.link}>Log in</button>
      </p>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Log In
// ══════════════════════════════════════

function LoginScreen({ onSuccess, onSwitchToSignUp }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      onSuccess(cred.user);
    } catch (err) {
      setError("Invalid email or password");
    }
    setLoading(false);
  };

  return (
    <div style={S.screen}>
      <p style={S.stepLabel}>LOG IN</p>
      <h2 style={S.title}>Welcome Back</h2>
      <p style={S.subtitle}>Pick up where you left off.</p>

      <label style={S.label}>Email</label>
      <input
        type="email"
        placeholder="you@university.edu"
        value={email}
        onChange={e => setEmail(e.target.value)}
        style={{ ...S.input, marginBottom: 16 }}
      />

      <label style={S.label}>Password</label>
      <input
        type="password"
        placeholder="Your password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        style={{ ...S.input, marginBottom: 8 }}
        onKeyDown={e => e.key === "Enter" && handleLogin()}
      />

      {error && <p style={S.error}>{error}</p>}

      <button
        onClick={handleLogin}
        disabled={loading || !email || !password}
        style={{
          ...S.btn, ...S.btnPrimary, marginTop: 24,
          opacity: (email && password && !loading) ? 1 : 0.4,
        }}
      >
        {loading ? "Logging in..." : "Log In"}
      </button>

      <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, textAlign: "center", marginTop: 24 }}>
        Don't have an account?{" "}
        <button onClick={onSwitchToSignUp} style={S.link}>Sign up</button>
      </p>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Verify Email
// ══════════════════════════════════════

function VerifyEmailScreen({ user, onVerified }) {
  const [checking, setChecking] = useState(false);
  const [resent, setResent] = useState(false);

  const checkVerification = async () => {
    setChecking(true);
    await user.reload();
    if (user.emailVerified) onVerified();
    else alert("Not verified yet. Check inbox and spam folder.");
    setChecking(false);
  };

  const resendEmail = async () => {
    await sendEmailVerification(user);
    setResent(true);
    setTimeout(() => setResent(false), 5000);
  };

  return (
    <div style={{
      ...S.screen, display: "flex", flexDirection: "column",
      justifyContent: "center", alignItems: "center", textAlign: "center",
    }}>
      <div style={{
        width: 80, height: 80, borderRadius: "50%",
        background: "rgba(233,69,96,0.1)", border: "2px solid rgba(233,69,96,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28,
      }}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#E94560" strokeWidth="1.5" strokeLinecap="round">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 4L12 13L2 4" />
        </svg>
      </div>

      <h2 style={S.title}>Check Your Email</h2>
      <p style={{ ...S.subtitle, maxWidth: 300 }}>
        We sent a verification link to <span style={{ color: "#fff" }}>{user.email}</span>.
        Click the link to verify, then come back here.
      </p>
      <div style={{
        background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.15)",
        borderRadius: 12, padding: "12px 16px", marginBottom: 20, maxWidth: 300,
      }}>
        <p style={{ color: "#E94560", fontSize: 13, margin: 0, lineHeight: 1.5 }}>
          ⚠️ Check your junk/spam folder if you don't see it within a minute. University emails often filter verification links.
        </p>
      </div>

      {resent && <p style={S.success}>Verification email resent!</p>}

      <button onClick={checkVerification} disabled={checking} style={{ ...S.btn, ...S.btnPrimary, width: 260, marginBottom: 12 }}>
        {checking ? "Checking..." : "I've Verified My Email"}
      </button>
      <button onClick={resendEmail} style={{ ...S.btn, ...S.btnSecondary, width: 260, marginBottom: 12 }}>
        Resend Email
      </button>
      <button onClick={() => signOut(auth)} style={{ ...S.link, marginTop: 16, color: "rgba(255,255,255,0.3)" }}>
        Sign out
      </button>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Create Profile
// ══════════════════════════════════════

function CreateProfileScreen({ user, onComplete }) {
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const toggleInterest = (interest) => {
    if (selected.includes(interest)) {
      setSelected(selected.filter(x => x !== interest));
    } else if (selected.length < 6) {
      setSelected([...selected, interest]);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Photo must be under 5MB");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const ready = name.trim().length > 0 && selected.length >= 3 && photoFile;

  const handleSave = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      // Upload photo
      let photoURL = "";
      if (photoFile) {
        const photoRef = ref(storage, `profile_photos/${user.uid}`);
        await uploadBytes(photoRef, photoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      await setDoc(doc(db, "users", user.uid), {
        name: name.trim(),
        bio: bio.trim(),
        interests: selected,
        email: user.email,
        photoURL: photoURL,
        createdAt: new Date().toISOString(),
        university: "Northeastern University",
      });
      onComplete({
        name: name.trim(),
        bio: bio.trim(),
        interests: selected,
        photoURL: photoURL,
        university: "Northeastern University",
      });
    } catch (err) {
      alert("Error saving profile: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ ...S.screen, overflowY: "auto", paddingBottom: 40 }}>
      <p style={S.stepLabel}>CREATE PROFILE</p>
      <h2 style={S.title}>Who Are You?</h2>
      <p style={S.subtitle}>Keep it light. The mystery is the point.</p>

      {/* Photo Upload */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
        <label style={{ cursor: "pointer", position: "relative" }}>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoSelect}
            style={{ display: "none" }}
          />
          {photoPreview ? (
            <img
              src={photoPreview}
              alt="Preview"
              style={{
                width: 100, height: 100, borderRadius: "50%",
                objectFit: "cover", border: "3px solid #E94560",
              }}
            />
          ) : (
            <div style={{
              width: 100, height: 100, borderRadius: "50%",
              border: "2px dashed rgba(255,255,255,0.15)",
              background: "rgba(255,255,255,0.03)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 4,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>Add Photo</span>
            </div>
          )}
          {photoPreview && (
            <div style={{
              position: "absolute", bottom: 0, right: 0,
              width: 28, height: 28, borderRadius: "50%",
              background: "#E94560", display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </div>
          )}
        </label>
      </div>
      {!photoFile && (
        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, textAlign: "center", marginBottom: 16, marginTop: -16 }}>
          Required — this is how people will find you
        </p>
      )}

      <label style={S.label}>First Name</label>
      <input
        type="text"
        placeholder="Just your first name"
        value={name}
        onChange={e => setName(e.target.value)}
        maxLength={20}
        style={{ ...S.input, marginBottom: 16 }}
      />

      <label style={S.label}>One-Liner Bio</label>
      <input
        type="text"
        placeholder="What makes you interesting in 10 words?"
        value={bio}
        onChange={e => setBio(e.target.value)}
        maxLength={60}
        style={{ ...S.input, marginBottom: 16 }}
      />

      <label style={S.label}>
        Interests{" "}
        <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>
          ({selected.length}/6 — pick at least 3)
        </span>
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        {INTERESTS.map(interest => {
          const active = selected.includes(interest);
          return (
            <button
              key={interest}
              onClick={() => toggleInterest(interest)}
              style={{
                padding: "8px 14px", borderRadius: 20, fontSize: 13,
                border: active ? "1.5px solid #E94560" : "1.5px solid rgba(255,255,255,0.1)",
                background: active ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.03)",
                color: active ? "#E94560" : "rgba(255,255,255,0.5)",
                transition: "all 0.2s",
                fontWeight: active ? 600 : 400,
              }}
            >
              {interest}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleSave}
        disabled={!ready || loading}
        style={{ ...S.btn, ...S.btnPrimary, marginTop: 32, opacity: ready && !loading ? 1 : 0.4 }}
      >
        {loading ? "Saving..." : "Start Exploring"}
      </button>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Home
// ══════════════════════════════════════

function HomeScreen({ user, profile, onTapPerson, incomingRequest, onViewRequest, pendingMatchName, onEditProfile }) {
  const [isOpen, setIsOpen] = useState(false);
  const [myLoc, setMyLoc] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [nearbyCount, setNearbyCount] = useState(0);
  const [locErr, setLocErr] = useState("");
  const [pulsing, setPulsing] = useState(false);

  // Listen to all open users
  useEffect(() => {
    const q = query(collection(db, "open_users"));
    const unsub = onSnapshot(q, (snapshot) => {
      const users = [];
      snapshot.forEach((d) => {
        if (d.id !== user.uid) users.push({ id: d.id, ...d.data() });
      });
      setNearbyCount(users.length);
      if (myLoc) {
        const filtered = users
          .map(u => ({ ...u, distance: getDistanceMiles(myLoc.lat, myLoc.lng, u.lat, u.lng) }))
          .filter(u => u.distance <= 0.25)
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 10);
        setNearby(filtered);
      }
    });
    return () => unsub();
  }, [user.uid, myLoc]);

  // Go Open
  const goOpen = async () => {
    setLocErr("");
    if (!navigator.geolocation) {
      setLocErr("No location support");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyLoc(loc);
        setIsOpen(true);
        setPulsing(true);
        try {
          await setDoc(doc(db, "open_users", user.uid), {
            name: profile.name,
            bio: profile.bio || "",
            interests: profile.interests,
            photoURL: profile.photoURL || "",
            lat: loc.lat,
            lng: loc.lng,
            university: profile.university || "Northeastern University",
            openedAt: new Date().toISOString(),
          });
        } catch (err) {
          setLocErr("Error: " + err.message);
        }
      },
      (err) => {
        setLocErr(err.code === 1
          ? "Location denied. Enable in browser settings."
          : "Location error. Try again.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Go Closed
  const goClose = async () => {
    setIsOpen(false);
    setPulsing(false);
    setNearby([]);
    try {
      await deleteDoc(doc(db, "open_users", user.uid));
    } catch (e) {}
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      deleteDoc(doc(db, "open_users", user.uid)).catch(() => {});
    };
  }, [user.uid]);

  return (
    <div style={{ ...S.screen, overflowY: "auto", paddingBottom: 40 }}>
      <GlobalStyles />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{
          fontSize: 22, fontWeight: 800, letterSpacing: -0.5,
          background: "linear-gradient(135deg, #FFFFFF 0%, #E94560 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          fontFamily: "'Playfair Display', Georgia, serif",
        }}>
          sidequest
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <VerifiedBadge />
          <div
            onClick={onEditProfile}
            style={{
              width: 34, height: 34, borderRadius: "50%",
              overflow: "hidden", cursor: "pointer",
              border: "2px solid #E94560",
            }}
          >
            <Avatar name={profile.name} photoURL={profile.photoURL} size={34} />
          </div>
        </div>
      </div>
      <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, marginBottom: 24 }}>
        Northeastern University · Boston, MA
      </p>

      {/* Incoming request banner */}
      {incomingRequest && (
        <div onClick={onViewRequest} style={{
          background: "rgba(233,69,96,0.12)", border: "1.5px solid rgba(233,69,96,0.3)",
          borderRadius: 14, padding: "14px 18px", marginBottom: 20,
          cursor: "pointer", animation: "fadeUp 0.4s ease-out",
        }}>
          <p style={{ color: "#E94560", fontSize: 14, fontWeight: 600, margin: "0 0 4px" }}>
            New Sidequest Request!
          </p>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0 }}>
            {incomingRequest.requesterName} wants to meet you. Tap to respond.
          </p>
        </div>
      )}

      {/* Pending match waiting */}
      {pendingMatchName && (
        <div style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 14, padding: "14px 18px", marginBottom: 20, textAlign: "center",
        }}>
          <div style={{
            width: 24, height: 24,
            border: "3px solid rgba(233,69,96,0.3)", borderTopColor: "#E94560",
            borderRadius: "50%", animation: "spin 0.8s linear infinite",
            margin: "0 auto 10px",
          }} />
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0 }}>
            Waiting for <span style={{ color: "#fff", fontWeight: 600 }}>{pendingMatchName}</span> to accept...
          </p>
        </div>
      )}

      {/* Campus social map */}
      {!isOpen && !pendingMatchName && nearbyCount > 0 && (
        <div style={{
          background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.15)",
          borderRadius: 12, padding: "10px 16px", marginBottom: 20,
          textAlign: "center", animation: "fadeUp 0.4s ease-out",
        }}>
          <p style={{ color: "#E94560", fontSize: 13, margin: 0, fontWeight: 500 }}>
            {nearbyCount} {nearbyCount === 1 ? "person is" : "people are"} on a sidequest nearby right now
          </p>
        </div>
      )}

      {/* Open/Closed toggle */}
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        padding: "40px 0", position: "relative",
      }}>
        {pulsing && [0, 1, 2].map(i => (
          <div key={i} style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: 150 + i * 60, height: 150 + i * 60,
            borderRadius: "50%", pointerEvents: "none",
            border: "1.5px solid rgba(233,69,96,0.2)",
            animation: `pulse 2.5s ease-out ${i * 0.6}s infinite`,
          }} />
        ))}
        <button
          onClick={isOpen ? goClose : goOpen}
          style={{
            width: 130, height: 130, borderRadius: "50%",
            background: isOpen
              ? "linear-gradient(135deg, #E94560 0%, #C73659 100%)"
              : "rgba(255,255,255,0.04)",
            border: isOpen ? "none" : "2px solid rgba(255,255,255,0.1)",
            cursor: "pointer", position: "relative", zIndex: 2,
            transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: isOpen ? "0 0 50px rgba(233,69,96,0.3)" : "none",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
          }}
        >
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: 2,
            color: isOpen ? "#fff" : "rgba(255,255,255,0.3)",
            textTransform: "uppercase",
          }}>
            {isOpen ? "OPEN" : "CLOSED"}
          </span>
        </button>
        <p style={{
          color: isOpen ? "#E94560" : "rgba(255,255,255,0.3)",
          fontSize: 13, marginTop: 20, textAlign: "center", transition: "color 0.3s",
        }}>
          {isOpen
            ? (nearby.length > 0
              ? `${nearby.length} ${nearby.length === 1 ? "person" : "people"} nearby`
              : "Searching for people nearby...")
            : "Tap to begin your sidequest"}
        </p>
      </div>

      {locErr && <p style={{ ...S.error, textAlign: "center", marginBottom: 16 }}>{locErr}</p>}

      {/* Nearby users list */}
      {isOpen && nearby.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <p style={{
            color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 600,
            letterSpacing: 1.5, marginBottom: 16, textTransform: "uppercase",
          }}>
            {nearby.length} {nearby.length === 1 ? "PERSON" : "PEOPLE"} NEARBY
          </p>
          {nearby.map((person, i) => {
            const shared = getSharedInterests(profile.interests, person.interests);
            return (
              <div
                key={person.id}
                onClick={() => onTapPerson(person)}
                style={{
                  background: "rgba(255,255,255,0.04)", borderRadius: 16,
                  padding: 18, marginBottom: 12,
                  border: "1px solid rgba(255,255,255,0.06)",
                  cursor: "pointer", transition: "all 0.2s",
                  animation: `fadeUp 0.4s ease-out ${i * 0.1}s both`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <Avatar name={person.name} photoURL={person.photoURL} size={48} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                      <span style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>
                        {person.name}
                      </span>
                      <VerifiedBadge />
                    </div>
                    <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, margin: 0 }}>
                      {person.university || "Northeastern"} · {getWalkTime(person.distance)}
                    </p>
                  </div>
                </div>
                {shared.length > 0 && (
                  <div style={{
                    marginTop: 12, padding: "8px 12px", borderRadius: 10,
                    background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.12)",
                  }}>
                    <p style={{ color: "#E94560", fontSize: 12, margin: 0, fontWeight: 500 }}>
                      Shared interest: {shared[0]}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {isOpen && nearby.length === 0 && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <div style={{
            width: 32, height: 32,
            border: "3px solid rgba(233,69,96,0.3)", borderTopColor: "#E94560",
            borderRadius: "50%", animation: "spin 0.8s linear infinite",
            margin: "0 auto 16px",
          }} />
          <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 13 }}>
            No one nearby yet. Hang tight or try during Quest Night.
          </p>
        </div>
      )}
      {/* Install prompt for iOS */}
      {!window.matchMedia('(display-mode: standalone)').matches && !window.navigator.standalone && (
        <div style={{
          background: "rgba(255,255,255,0.04)", borderRadius: 14,
          padding: "14px 18px", marginTop: 32,
          border: "1px solid rgba(255,255,255,0.06)", textAlign: "center",
        }}>
          <p style={{ color: "#fff", fontSize: 14, fontWeight: 600, margin: "0 0 6px" }}>
            Install Sidequest
          </p>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            Tap the share button ↗ then "Add to Home Screen" for the full experience.
          </p>
        </div>
      )}
      {/* Edit Profile & Sign Out */}
      <button
        onClick={onEditProfile}
        style={{ ...S.btn, ...S.btnSecondary, marginTop: 32 }}
      >
        Edit Profile
      </button>
      <button
        onClick={async () => { await goClose(); await signOut(auth); }}
        style={{ ...S.btn, ...S.btnSecondary, marginTop: 10 }}
      >
        Sign Out
      </button>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Match Preview
// ══════════════════════════════════════

function MatchPreviewScreen({ person, profile, onConfirm, onDecline }) {
  const shared = getSharedInterests(profile.interests, person.interests);
  const iceBreaker = shared.length > 0 ? getIceBreaker(shared[0]) : getIceBreaker(null);

  return (
    <div style={{ ...S.screen, display: "flex", flexDirection: "column" }}>
      <GlobalStyles />
      <p style={{ ...S.stepLabel, textAlign: "center" }}>NEW SIDEQUEST</p>
      <h2 style={{ ...S.title, textAlign: "center", fontSize: 26, marginBottom: 32 }}>
        Ready for an adventure?
      </h2>

      <div style={{
        background: "rgba(255,255,255,0.04)", borderRadius: 20,
        padding: 28, border: "1px solid rgba(255,255,255,0.06)", textAlign: "center",
      }}>
        <div style={{ margin: "0 auto 16px", border: "3px solid rgba(233, 69, 96, 0.3)", borderRadius: 16, overflow: "hidden", width: "100%", maxWidth: 280, height: 280 }}>
          {person.photoURL ? (
            <img src={person.photoURL} alt={person.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Avatar name={person.name} photoURL={null} size={280} />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>{person.name}</span>
          <VerifiedBadge />
        </div>

        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, margin: "0 0 4px" }}>
          {person.university || "Northeastern University"}
        </p>
        {person.bio && (
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, margin: "0 0 20px", fontStyle: "italic" }}>
            "{person.bio}"
          </p>
        )}

        <div style={{
          background: "rgba(233,69,96,0.08)", borderRadius: 12,
          padding: 16, border: "1px solid rgba(233,69,96,0.12)",
        }}>
          <p style={{
            color: "rgba(255,255,255,0.35)", fontSize: 10,
            fontWeight: 600, letterSpacing: 1.5, margin: "0 0 8px",
          }}>
            ICE BREAKER
          </p>
          <p style={{ color: "#fff", fontSize: 14, margin: 0, lineHeight: 1.5 }}>
            {iceBreaker}
          </p>
        </div>
      </div>

      <div style={{
        background: "rgba(255,255,255,0.04)", borderRadius: 12,
        padding: "12px 16px", marginTop: 16,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E94560" strokeWidth="2" strokeLinecap="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0 }}>
          {getWalkTime(person.distance)} away
        </p>
      </div>

      <div style={{ marginTop: "auto", paddingTop: 24, paddingBottom: 20 }}>
        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 12, textAlign: "center", marginBottom: 16 }}>
          Both of you must opt in to connect
        </p>
        <button onClick={onConfirm} style={{ ...S.btn, ...S.btnPrimary }}>
          Let's Meet
        </button>
        <button onClick={onDecline} style={{ ...S.btn, ...S.btnSecondary, marginTop: 10 }}>
          Not Right Now
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Incoming Request
// ══════════════════════════════════════

function IncomingRequestScreen({ match, myUid, onAccept, onDecline }) {
  const otherName = match.requester === myUid ? match.requesteeName : match.requesterName;
  const shared = getSharedInterests(match.requesterInterests, match.requesteeInterests);
  const iceBreaker = shared.length > 0 ? getIceBreaker(shared[0]) : getIceBreaker(null);

  return (
    <div style={{ ...S.screen, display: "flex", flexDirection: "column" }}>
      <GlobalStyles />
      <p style={{ ...S.stepLabel, textAlign: "center" }}>INCOMING SIDEQUEST</p>
      <h2 style={{ ...S.title, textAlign: "center", fontSize: 26, marginBottom: 32 }}>
        Someone wants to meet you!
      </h2>

      <div style={{
        background: "rgba(255,255,255,0.04)", borderRadius: 20,
        padding: 28, border: "1px solid rgba(255,255,255,0.06)", textAlign: "center",
      }}>
        <div style={{ margin: "0 auto 16px", border: "3px solid rgba(233, 69, 96, 0.3)", borderRadius: 16, overflow: "hidden", width: "100%", maxWidth: 280, height: 280 }}>
          {(match.requester === myUid ? match.requesteePhoto : match.requesterPhoto) ? (
            <img src={match.requester === myUid ? match.requesteePhoto : match.requesterPhoto} alt={otherName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Avatar name={otherName} photoURL={null} size={280} />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>{otherName}</span>
          <VerifiedBadge />
        </div>

        {shared.length > 0 && (
          <div style={{
            background: "rgba(233,69,96,0.08)", borderRadius: 12,
            padding: 16, border: "1px solid rgba(233,69,96,0.12)",
          }}>
            <p style={{
              color: "rgba(255,255,255,0.35)", fontSize: 10,
              fontWeight: 600, letterSpacing: 1.5, margin: "0 0 8px",
            }}>
              ICE BREAKER
            </p>
            <p style={{ color: "#fff", fontSize: 14, margin: 0, lineHeight: 1.5 }}>
              {iceBreaker}
            </p>
          </div>
        )}
      </div>

      <div style={{ marginTop: "auto", paddingTop: 24, paddingBottom: 20 }}>
        <button onClick={onAccept} style={{ ...S.btn, ...S.btnPrimary }}>
          Accept Quest
        </button>
        <button onClick={onDecline} style={{ ...S.btn, ...S.btnSecondary, marginTop: 10 }}>
          Decline
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Shirt Picker
// ══════════════════════════════════════

function ShirtPickerScreen({ match, myUid, onPicked }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const otherName = match.requester === myUid ? match.requesteeName : match.requesterName;

  const handlePick = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const field = match.requester === myUid ? "requesterShirt" : "requesteeShirt";
      await updateDoc(doc(db, "matches", match.id), {
        [field]: { name: selected.name, hex: selected.hex },
      });
      onPicked();
    } catch (err) {
      alert("Error: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ ...S.screen, display: "flex", flexDirection: "column" }}>
      <GlobalStyles />
      <p style={{ ...S.stepLabel, textAlign: "center" }}>ALMOST THERE</p>
      <h2 style={{ ...S.title, textAlign: "center", fontSize: 24, marginBottom: 8 }}>
        Help {otherName} find you
      </h2>
      <p style={{ ...S.subtitle, textAlign: "center" }}>
        What color shirt are you wearing right now?
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 32 }}>
        {SHIRT_COLORS.map(color => (
          <button
            key={color.name}
            onClick={() => setSelected(color)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
              padding: "14px 8px", borderRadius: 14, cursor: "pointer",
              border: selected?.name === color.name
                ? `2px solid ${color.border}`
                : "1.5px solid rgba(255,255,255,0.06)",
              background: selected?.name === color.name
                ? "rgba(255,255,255,0.06)"
                : "rgba(255,255,255,0.02)",
              transition: "all 0.2s",
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: color.hex,
              border: color.name === "Black" ? "1px solid #444" : "none",
              boxShadow: selected?.name === color.name ? `0 0 12px ${color.hex}44` : "none",
            }} />
            <span style={{
              fontSize: 11,
              color: selected?.name === color.name ? "#fff" : "rgba(255,255,255,0.35)",
              fontWeight: selected?.name === color.name ? 600 : 400,
            }}>
              {color.name}
            </span>
          </button>
        ))}
      </div>

      {selected && (
        <div style={{
          background: "rgba(255,255,255,0.04)", borderRadius: 12,
          padding: 16, marginBottom: 24, textAlign: "center",
          animation: "fadeUp 0.3s ease-out",
        }}>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0 }}>
            {otherName} will see:{" "}
            <span style={{ color: "#fff", fontWeight: 600 }}>
              "Look for someone in a {selected.name.toLowerCase()} shirt"
            </span>
          </p>
        </div>
      )}

      <div style={{ marginTop: "auto", paddingBottom: 20 }}>
        <button
          onClick={handlePick}
          disabled={!selected || loading}
          style={{ ...S.btn, ...S.btnPrimary, opacity: selected && !loading ? 1 : 0.4 }}
        >
          {loading ? "Saving..." : "I'm Ready"}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Waiting for Shirt
// ══════════════════════════════════════

function WaitingForShirtScreen({ match, myUid }) {
  const otherName = match.requester === myUid ? match.requesteeName : match.requesterName;

  return (
    <div style={{
      ...S.screen, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", textAlign: "center",
    }}>
      <GlobalStyles />
      <div style={{
        width: 32, height: 32,
        border: "3px solid rgba(233,69,96,0.3)", borderTopColor: "#E94560",
        borderRadius: "50%", animation: "spin 0.8s linear infinite",
        marginBottom: 24,
      }} />
      <h2 style={{ ...S.title, fontSize: 22 }}>You're set!</h2>
      <p style={{ ...S.subtitle, maxWidth: 280 }}>
        Waiting for <span style={{ color: "#fff" }}>{otherName}</span> to pick their shirt color...
      </p>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Meeting
// ══════════════════════════════════════

function MeetingScreen({ match, myUid, onEndSession }) {
  const [minutes, setMinutes] = useState(15);
  const [seconds, setSeconds] = useState(0);
  const [msgInput, setMsgInput] = useState("");
  const [messages, setMessages] = useState(match.messages || []);

  const isRequester = match.requester === myUid;
  const otherName = isRequester ? match.requesteeName : match.requesterName;
  const myShirt = isRequester ? match.requesterShirt : match.requesteeShirt;
  const theirShirt = isRequester ? match.requesteeShirt : match.requesterShirt;
  const myMsgCount = messages.filter(m => m.sender === myUid).length;
  const msgsLeft = 2 - myMsgCount;

  // Timer
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(prev => {
        if (prev === 0) {
          if (minutes === 0) { clearInterval(interval); return 0; }
          setMinutes(m => m - 1);
          return 59;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Listen for messages
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "matches", match.id), (snap) => {
      if (snap.exists()) {
        setMessages(snap.data().messages || []);
      }
    });
    return () => unsub();
  }, [match.id]);

  // Send message
  const sendMessage = async () => {
    if (!msgInput.trim() || msgsLeft <= 0) return;
    const newMsg = {
      sender: myUid,
      text: msgInput.trim(),
      time: new Date().toISOString(),
    };
    try {
      await updateDoc(doc(db, "matches", match.id), {
        messages: [...messages, newMsg],
      });
      setMsgInput("");
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  return (
    <div style={{ ...S.screen, display: "flex", flexDirection: "column" }}>
      <GlobalStyles />

      {/* Emergency button */}
      <div style={{
        position: "absolute", top: 20, right: 20, zIndex: 10,
        background: "rgba(233,69,96,0.12)", borderRadius: 20,
        padding: "6px 14px", display: "flex", alignItems: "center",
        gap: 6, cursor: "pointer",
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#E94560" strokeWidth="2.5">
          <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
        </svg>
        <span style={{ color: "#E94560", fontSize: 11, fontWeight: 600 }}>Emergency</span>
      </div>

      <h2 style={{ ...S.title, textAlign: "center", fontSize: 22, marginTop: 20, marginBottom: 6 }}>
        Go meet {otherName}!
      </h2>
      <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 13, textAlign: "center", marginBottom: 20 }}>
        Put your phone down — this is the good part.
      </p>

      {/* Assigned Location with Directions */}
      {match.location && (
        <div style={{
          background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.15)",
          borderRadius: 14, padding: "14px 18px", marginBottom: 16, textAlign: "center",
        }}>
          <p style={{
            color: "rgba(255,255,255,0.35)", fontSize: 10,
            fontWeight: 600, letterSpacing: 1.5, margin: "0 0 6px",
          }}>
            YOUR SIDEQUEST LOCATION
          </p>
          <p style={{ color: "#fff", fontSize: 16, fontWeight: 600, margin: "0 0 10px" }}>
            {match.location.name}
          </p>
          {match.location.lat && (
            <button
              onClick={() => window.open(
                `https://www.google.com/maps/dir/?api=1&destination=${match.location.lat},${match.location.lng}&travelmode=walking`,
                "_blank"
              )}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 10, padding: "8px 16px",
                color: "#fff", fontSize: 13, fontWeight: 500,
                cursor: "pointer", display: "inline-flex",
                alignItems: "center", gap: 6,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E94560" strokeWidth="2" strokeLinecap="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Get Walking Directions
            </button>
          )}
        </div>
      )}

      {/* Shirt identifier cards */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {/* Their shirt */}
        <div style={{
          flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 14,
          padding: 16, border: "1px solid rgba(255,255,255,0.06)", textAlign: "center",
        }}>
          <p style={{
            color: "rgba(255,255,255,0.35)", fontSize: 10,
            fontWeight: 600, letterSpacing: 1.5, margin: "0 0 10px",
          }}>
            LOOK FOR
          </p>
          <Avatar
            name={otherName}
            photoURL={isRequester ? match.requesteePhoto : match.requesterPhoto}
            size={64}
          />
          <p style={{ color: "#fff", fontSize: 14, fontWeight: 600, margin: "8px 0 0" }}>
            {otherName}
          </p>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 6, marginTop: 6,
          }}>
            <div style={{
              width: 16, height: 16, borderRadius: "50%",
              background: theirShirt?.hex || "#666",
              border: theirShirt?.name === "Black" ? "1px solid #444" : "none",
            }} />
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, margin: 0 }}>
              {theirShirt?.name || "?"} shirt
            </p>
          </div>
        </div>

        {/* My shirt */}
        <div style={{
          flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 14,
          padding: 16, border: "1px solid rgba(255,255,255,0.06)", textAlign: "center",
        }}>
          <p style={{
            color: "rgba(255,255,255,0.35)", fontSize: 10,
            fontWeight: 600, letterSpacing: 1.5, margin: "0 0 10px",
          }}>
            YOU
          </p>
          <div style={{
            width: 44, height: 44, borderRadius: "50%",
            background: myShirt?.hex || "#666",
            margin: "0 auto 8px",
            boxShadow: `0 0 16px ${myShirt?.hex || "#666"}44`,
            border: myShirt?.name === "Black" ? "1px solid #444" : "none",
          }} />
          <p style={{ color: "#fff", fontSize: 14, fontWeight: 600, margin: 0 }}>You</p>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, margin: "2px 0 0" }}>
            {myShirt?.name || "?"} shirt
          </p>
        </div>
      </div>

      {/* 2-Message System */}
      <div style={{
        background: "rgba(255,255,255,0.03)", borderRadius: 14,
        border: "1px solid rgba(255,255,255,0.06)", padding: 16,
        marginBottom: 16, flex: 1, minHeight: 120,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 12,
        }}>
          <p style={{
            color: "rgba(255,255,255,0.35)", fontSize: 10,
            fontWeight: 600, letterSpacing: 1.5, margin: 0,
          }}>
            QUICK MESSAGES
          </p>
          <span style={{
            fontSize: 11,
            color: msgsLeft > 0 ? "rgba(255,255,255,0.3)" : "#E94560",
          }}>
            {msgsLeft}/2 left
          </span>
        </div>

        {/* Message list */}
        <div style={{ flex: 1, overflowY: "auto", marginBottom: 12 }}>
          {messages.map((msg, i) => {
            const isMine = msg.sender === myUid;
            return (
              <div
                key={i}
                style={{
                  background: isMine ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.06)",
                  borderRadius: isMine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  padding: "10px 14px", marginBottom: 8, maxWidth: "85%",
                  marginLeft: isMine ? "auto" : 0,
                  animation: "fadeUp 0.3s ease-out",
                }}
              >
                <p style={{
                  color: isMine ? "rgba(233,69,96,0.6)" : "rgba(255,255,255,0.4)",
                  fontSize: 10, fontWeight: 600, margin: "0 0 4px",
                  textAlign: isMine ? "right" : "left",
                }}>
                  {isMine ? "You" : otherName}
                </p>
                <p style={{ color: "#fff", fontSize: 13, margin: 0, lineHeight: 1.4 }}>
                  {msg.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Message input */}
        {msgsLeft > 0 ? (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              placeholder={msgsLeft === 2 ? 'e.g. "I\'m at the table by the door"' : "One more message..."}
              value={msgInput}
              onChange={e => setMsgInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage()}
              maxLength={80}
              style={{ ...S.input, flex: 1, padding: "10px 14px", fontSize: 13, borderRadius: 20 }}
            />
            <button
              onClick={sendMessage}
              disabled={!msgInput.trim()}
              style={{
                width: 40, height: 40, borderRadius: "50%", border: "none", flexShrink: 0,
                background: msgInput.trim() ? "#E94560" : "rgba(255,255,255,0.06)",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        ) : (
          <p style={{
            color: "rgba(255,255,255,0.25)", fontSize: 12,
            textAlign: "center", fontStyle: "italic", margin: 0,
          }}>
            No messages left — time to find each other!
          </p>
        )}
      </div>

      {/* Timer */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 12, marginBottom: 16,
      }}>
        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>Check-in in</span>
        <span style={{ fontSize: 22, fontWeight: 300, color: "#fff", fontVariantNumeric: "tabular-nums" }}>
          {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </span>
      </div>

      <button onClick={onEndSession} style={{ ...S.btn, ...S.btnSecondary, marginBottom: 20 }}>
        End Session
      </button>
    </div>
  );
}

// ══════════════════════════════════════
//  SCREEN: Check-In
// ══════════════════════════════════════

function CheckInScreen({ match, myUid, onDone }) {
  const [rating, setRating] = useState(null);
  const [safe, setSafe] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setSubmitted(true);
    const isRequester = match.requester === myUid;
    try {
      const field = isRequester ? "requesterFeedback" : "requesteeFeedback";
      await updateDoc(doc(db, "matches", match.id), {
        [field]: { rating, safe, submittedAt: new Date().toISOString() },
      });
      await deleteDoc(doc(db, "open_users", myUid)).catch(() => {});
    } catch (e) {}
    setTimeout(() => onDone(), 500);
  };

  return (
    <div style={{
      ...S.screen, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
    }}>
      <GlobalStyles />

      <h2 style={{ ...S.title, textAlign: "center", fontSize: 24, marginBottom: 8 }}>
        How was your sidequest?
      </h2>
      <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 14, marginBottom: 40 }}>
        Your feedback keeps the community safe.
      </p>

      {/* Rating */}
      <p style={{
        color: "rgba(255,255,255,0.5)", fontSize: 13,
        fontWeight: 600, letterSpacing: 1, marginBottom: 16,
      }}>
        HOW'D IT GO?
      </p>
      <div style={{ display: "flex", gap: 16, marginBottom: 40 }}>
        {["👎", "😐", "👍", "🔥"].map((emoji, i) => (
          <button
            key={i}
            onClick={() => setRating(i)}
            style={{
              width: 60, height: 60, borderRadius: 16, fontSize: 28,
              border: rating === i ? "2px solid #E94560" : "1px solid rgba(255,255,255,0.08)",
              background: rating === i ? "rgba(233,69,96,0.12)" : "rgba(255,255,255,0.03)",
              cursor: "pointer", transition: "all 0.2s",
              transform: rating === i ? "scale(1.1)" : "scale(1)",
            }}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Safety */}
      <p style={{
        color: "rgba(255,255,255,0.5)", fontSize: 13,
        fontWeight: 600, letterSpacing: 1, marginBottom: 16,
      }}>
        DID YOU FEEL SAFE?
      </p>
      <div style={{ display: "flex", gap: 12, marginBottom: 40 }}>
        {[{ label: "Yes", val: true }, { label: "No", val: false }].map(opt => (
          <button
            key={opt.label}
            onClick={() => setSafe(opt.val)}
            style={{
              padding: "12px 32px", borderRadius: 12, fontSize: 15, fontWeight: 500,
              border: safe === opt.val ? "2px solid" : "1px solid rgba(255,255,255,0.08)",
              borderColor: safe === opt.val
                ? (opt.val ? "#2ED589" : "#E94560")
                : "rgba(255,255,255,0.08)",
              background: safe === opt.val
                ? (opt.val ? "rgba(46,213,137,0.12)" : "rgba(233,69,96,0.12)")
                : "rgba(255,255,255,0.03)",
              color: safe === opt.val
                ? (opt.val ? "#2ED589" : "#E94560")
                : "rgba(255,255,255,0.4)",
              cursor: "pointer", transition: "all 0.2s",
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Safety flag warning */}
      {safe === false && (
        <div style={{
          background: "rgba(233,69,96,0.08)", border: "1px solid rgba(233,69,96,0.2)",
          borderRadius: 12, padding: 16, marginBottom: 24,
          width: "100%", maxWidth: 320, animation: "fadeUp 0.3s ease-out",
        }}>
          <p style={{ color: "#E94560", fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            We take this seriously. This user has been flagged for review.
          </p>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={rating === null || safe === null || submitted}
        style={{
          ...S.btn, ...S.btnPrimary, width: 220,
          opacity: (rating !== null && safe !== null && !submitted) ? 1 : 0.4,
        }}
      >
        Done
      </button>
    </div>
  );
}
// ══════════════════════════════════════
//  SCREEN: Edit Profile
// ══════════════════════════════════════

function EditProfileScreen({ user, profile, onSave, onCancel }) {
  const [name, setName] = useState(profile.name || "");
  const [bio, setBio] = useState(profile.bio || "");
  const [selected, setSelected] = useState(profile.interests || []);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(profile.photoURL || null);
  const [loading, setLoading] = useState(false);

  const toggleInterest = (interest) => {
    if (selected.includes(interest)) {
      setSelected(selected.filter(x => x !== interest));
    } else if (selected.length < 6) {
      setSelected([...selected, interest]);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Photo must be under 5MB");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const ready = name.trim().length > 0 && selected.length >= 3;

  const handleSave = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      let photoURL = profile.photoURL || "";

      // Upload new photo if one was selected
      if (photoFile) {
        const photoRef = ref(storage, `profile_photos/${user.uid}`);
        await uploadBytes(photoRef, photoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      const updatedProfile = {
        name: name.trim(),
        bio: bio.trim(),
        interests: selected,
        email: user.email,
        photoURL: photoURL,
        createdAt: profile.createdAt || new Date().toISOString(),
        university: "Northeastern University",
      };

      await setDoc(doc(db, "users", user.uid), updatedProfile);
      onSave(updatedProfile);
    } catch (err) {
      alert("Error saving: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ ...S.screen, overflowY: "auto", paddingBottom: 40 }}>
      <GlobalStyles />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <button onClick={onCancel} style={{ ...S.link, fontSize: 14 }}>
          ← Back
        </button>
        <p style={{ ...S.stepLabel, margin: 0 }}>EDIT PROFILE</p>
        <div style={{ width: 40 }} />
      </div>

      {/* Photo */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
        <label style={{ cursor: "pointer", position: "relative" }}>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoSelect}
            style={{ display: "none" }}
          />
          {photoPreview ? (
            <img
              src={photoPreview}
              alt="Preview"
              style={{
                width: 100, height: 100, borderRadius: "50%",
                objectFit: "cover", border: "3px solid #E94560",
              }}
            />
          ) : (
            <div style={{
              width: 100, height: 100, borderRadius: "50%",
              border: "2px dashed rgba(255,255,255,0.15)",
              background: "rgba(255,255,255,0.03)",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 4,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>Add Photo</span>
            </div>
          )}
          <div style={{
            position: "absolute", bottom: 0, right: 0,
            width: 28, height: 28, borderRadius: "50%",
            background: "#E94560", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
              <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
            </svg>
          </div>
        </label>
      </div>

      {/* Name */}
      <label style={S.label}>First Name</label>
      <input
        type="text"
        placeholder="Just your first name"
        value={name}
        onChange={e => setName(e.target.value)}
        maxLength={20}
        style={{ ...S.input, marginBottom: 16 }}
      />

      {/* Bio */}
      <label style={S.label}>One-Liner Bio</label>
      <input
        type="text"
        placeholder="What makes you interesting in 10 words?"
        value={bio}
        onChange={e => setBio(e.target.value)}
        maxLength={60}
        style={{ ...S.input, marginBottom: 16 }}
      />

      {/* Interests */}
      <label style={S.label}>
        Interests{" "}
        <span style={{ color: "rgba(255,255,255,0.3)", fontWeight: 400 }}>
          ({selected.length}/6 — pick at least 3)
        </span>
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        {INTERESTS.map(interest => {
          const active = selected.includes(interest);
          return (
            <button
              key={interest}
              onClick={() => toggleInterest(interest)}
              style={{
                padding: "8px 14px", borderRadius: 20, fontSize: 13,
                border: active ? "1.5px solid #E94560" : "1.5px solid rgba(255,255,255,0.1)",
                background: active ? "rgba(233,69,96,0.15)" : "rgba(255,255,255,0.03)",
                color: active ? "#E94560" : "rgba(255,255,255,0.5)",
                transition: "all 0.2s",
                fontWeight: active ? 600 : 400,
              }}
            >
              {interest}
            </button>
          );
        })}
      </div>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={!ready || loading}
        style={{ ...S.btn, ...S.btnPrimary, marginTop: 32, opacity: ready && !loading ? 1 : 0.4 }}
      >
        {loading ? "Saving..." : "Save Changes"}
      </button>
    </div>
  );
}
// ══════════════════════════════════════
//  MAIN APP
// ══════════════════════════════════════

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [screen, setScreen] = useState("splash");
  const [loading, setLoading] = useState(true);

  // Match state
  const [activeMatch, setActiveMatch] = useState(null);
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [incomingRequest, setIncomingRequest] = useState(null);
  const [pendingMatchName, setPendingMatchName] = useState(null);

  // ── Auth listener ──
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const profileDoc = await getDoc(doc(db, "users", firebaseUser.uid));
        if (profileDoc.exists()) {
          setProfile(profileDoc.data());
          setScreen("home");
        } else if (!firebaseUser.emailVerified) {
          setScreen("verify");
        } else {
          setScreen("profile");
        }
      } else {
        setUser(null);
        setProfile(null);
        setScreen("splash");
        setActiveMatch(null);
        setActiveMatchId(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // ── Listen for incoming requests (I'm the requestee) ──
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "matches"),
      where("requestee", "==", user.uid),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const d = snap.docs[0];
        setIncomingRequest({ id: d.id, ...d.data() });
      } else {
        setIncomingRequest(null);
      }
    });
    return () => unsub();
  }, [user]);

  // ── Listen for pending requests I sent ──
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "matches"),
      where("requester", "==", user.uid),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        const d = snap.docs[0];
        setPendingMatchName(d.data().requesteeName);
        if (!activeMatchId) setActiveMatchId(d.id);
      } else {
        setPendingMatchName(null);
      }
    });
    return () => unsub();
  }, [user, activeMatchId]);

  // ── Listen to active match document ──
  useEffect(() => {
    if (!activeMatchId || !user) {
      setActiveMatch(null);
      return;
    }
    const unsub = onSnapshot(doc(db, "matches", activeMatchId), async (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setActiveMatch(data);

        if (data.status === "accepted") {
          const isRequester = data.requester === user.uid;
          const myShirt = isRequester ? data.requesterShirt : data.requesteeShirt;
          const theirShirt = isRequester ? data.requesteeShirt : data.requesterShirt;

          if (!myShirt) {
            setScreen("shirt_pick");
          } else if (!theirShirt) {
            setScreen("waiting_shirt");
          } else {
            // Both picked — transition to active
            try {
              await updateDoc(doc(db, "matches", activeMatchId), { status: "active" });
            } catch (e) {}
            setScreen("meeting");
          }
        } else if (data.status === "active") {
          setScreen("meeting");
        } else if (data.status === "completed") {
          if (screen !== "checkin") {
            setScreen("home");
            setActiveMatch(null);
            setActiveMatchId(null);
          }
        }
      }
    });
    return () => unsub();
  }, [activeMatchId, user]);

  // ── Handlers ──

  const handleTapPerson = (person) => {
    setSelectedPerson(person);
    setScreen("match_preview");
  };

  const handleSendRequest = async () => {
    if (!selectedPerson || !user || !profile) return;
    const shared = getSharedInterests(profile.interests, selectedPerson.interests);
    try {
      const matchRef = await addDoc(collection(db, "matches"), {
        requester: user.uid,
        requestee: selectedPerson.id,
        requesterName: profile.name,
        requesteeName: selectedPerson.name,
        requesterPhoto: profile.photoURL || "",
        requesteePhoto: selectedPerson.photoURL || "",
        requesterInterests: profile.interests,
        requesteeInterests: selectedPerson.interests || [],
        sharedInterest: shared.length > 0 ? shared[0] : null,
        iceBreaker: shared.length > 0 ? getIceBreaker(shared[0]) : getIceBreaker(null),
        status: "pending",
        location: null,
        requesterShirt: null,
        requesteeShirt: null,
        messages: [],
        createdAt: new Date().toISOString(),
      });
      setActiveMatchId(matchRef.id);
      setPendingMatchName(selectedPerson.name);
      setSelectedPerson(null);
      setScreen("home");
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleAcceptRequest = async () => {
    if (!incomingRequest) return;
    try {
      // Get both locations to assign meetup spot
      const requesterDoc = await getDoc(doc(db, "open_users", incomingRequest.requester));
      const requesteeDoc = await getDoc(doc(db, "open_users", incomingRequest.requestee));

      let location = CAMPUS_SPOTS[Math.floor(Math.random() * CAMPUS_SPOTS.length)];
      if (requesterDoc.exists() && requesteeDoc.exists()) {
        location = assignMeetupSpot(
          requesterDoc.data().lat, requesterDoc.data().lng,
          requesteeDoc.data().lat, requesteeDoc.data().lng
        );
      }

      await updateDoc(doc(db, "matches", incomingRequest.id), {
        status: "accepted",
        location: { name: location.name, lat: location.lat, lng: location.lng },
      });
      setActiveMatchId(incomingRequest.id);
      setIncomingRequest(null);
      setScreen("shirt_pick");
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleDeclineRequest = async () => {
    if (!incomingRequest) return;
    try {
      await updateDoc(doc(db, "matches", incomingRequest.id), { status: "completed" });
    } catch (e) {}
    setIncomingRequest(null);
  };

  const handleShirtPicked = () => {
    setScreen("waiting_shirt");
  };

  const handleEndSession = () => {
    setScreen("checkin");
  };

  const handleCheckInDone = () => {
    setActiveMatch(null);
    setActiveMatchId(null);
    setScreen("home");
  };

  // ── Loading ──
  if (loading) {
    return (
      <div style={{
        ...S.screen, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          width: 32, height: 32,
          border: "3px solid rgba(233,69,96,0.3)", borderTopColor: "#E94560",
          borderRadius: "50%", animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Screen Router ──
  return (
    <div>
      {screen === "splash" && (
        <SplashScreen
          onSignUp={() => setScreen("signup")}
          onLogIn={() => setScreen("login")}
        />
      )}

      {screen === "signup" && (
        <SignUpScreen
          onSuccess={(u) => { setUser(u); setScreen("verify"); }}
          onSwitchToLogin={() => setScreen("login")}
        />
      )}

      {screen === "login" && (
        <LoginScreen
          onSuccess={(u) => setUser(u)}
          onSwitchToSignUp={() => setScreen("signup")}
        />
      )}

      {screen === "verify" && user && (
        <VerifyEmailScreen
          user={user}
          onVerified={() => setScreen("profile")}
        />
      )}

      {screen === "profile" && user && (
        <CreateProfileScreen
          user={user}
          onComplete={(p) => { setProfile(p); setScreen("home"); }}
        />
      )}

      {screen === "home" && user && profile && (
        <HomeScreen
          user={user}
          profile={profile}
          onTapPerson={handleTapPerson}
          incomingRequest={incomingRequest}
          onViewRequest={() => setScreen("incoming_request")}
          pendingMatchName={pendingMatchName}
          onEditProfile={() => setScreen("edit_profile")}
        />
      )}
      {screen === "edit_profile" && user && profile && (
        <EditProfileScreen
          user={user}
          profile={profile}
          onSave={(updatedProfile) => { setProfile(updatedProfile); setScreen("home"); }}
          onCancel={() => setScreen("home")}
        />
      )}
      {screen === "match_preview" && selectedPerson && profile && (
        <MatchPreviewScreen
          person={selectedPerson}
          profile={profile}
          onConfirm={handleSendRequest}
          onDecline={() => { setSelectedPerson(null); setScreen("home"); }}
        />
      )}

      {screen === "incoming_request" && incomingRequest && (
        <IncomingRequestScreen
          match={incomingRequest}
          myUid={user.uid}
          onAccept={handleAcceptRequest}
          onDecline={() => { handleDeclineRequest(); setScreen("home"); }}
        />
      )}

      {screen === "shirt_pick" && activeMatch && user && (
        <ShirtPickerScreen
          match={activeMatch}
          myUid={user.uid}
          onPicked={handleShirtPicked}
        />
      )}

      {screen === "waiting_shirt" && activeMatch && user && (
        <WaitingForShirtScreen
          match={activeMatch}
          myUid={user.uid}
        />
      )}

      {screen === "meeting" && activeMatch && user && (
        <MeetingScreen
          match={activeMatch}
          myUid={user.uid}
          onEndSession={handleEndSession}
        />
      )}

      {screen === "checkin" && activeMatch && user && (
        <CheckInScreen
          match={activeMatch}
          myUid={user.uid}
          onDone={handleCheckInDone}
        />
      )}
    </div>
  );
}
